/**
 * Password Policy Enforcement for MyClinicCore
 * HIPAA requires strong password policies for systems handling PHI.
 *
 * Policy:
 * - Minimum 12 characters
 * - At least 1 uppercase letter
 * - At least 1 lowercase letter
 * - At least 1 digit
 * - At least 1 special character
 * - Not in common passwords list
 */

const COMMON_PASSWORDS = new Set([
  'password1234', 'password12345', 'passw0rd1234', 'qwerty123456',
  'admin1234567', 'letmein12345', 'welcome12345', 'monkey1234567',
  'dragon123456', 'master123456', 'login1234567', 'princess12345',
  'abc123456789', 'password!234', 'iloveyou1234', 'trustno1!234',
  'sunshine12345', 'shadow123456', 'ashley123456', 'football12345',
  'jesus1234567', 'michael12345', 'charlie12345', 'andrew123456',
  'jessica12345', 'superman12345', 'tigger123456', 'jennifer12345',
  'hunter123456', 'buster123456', 'soccer123456', 'batman123456',
  'testing12345', 'changeme12345', 'p@ssword1234', 'p@ssw0rd1234',
  'password!@#$', 'qwerty!@#$%^', '123456789012', 'abcdefghijkl',
]);

export interface PasswordCheck {
  label: string;
  met: boolean;
}

export interface PasswordValidationResult {
  isValid: boolean;
  score: number; // 0-5 (one point per check)
  checks: PasswordCheck[];
  strengthLabel: string;
}

export function validatePassword(password: string): PasswordValidationResult {
  const checks: PasswordCheck[] = [
    {
      label: 'At least 12 characters',
      met: password.length >= 12,
    },
    {
      label: 'Contains uppercase letter',
      met: /[A-Z]/.test(password),
    },
    {
      label: 'Contains lowercase letter',
      met: /[a-z]/.test(password),
    },
    {
      label: 'Contains a number',
      met: /\d/.test(password),
    },
    {
      label: 'Contains special character (!@#$%^&*...)',
      met: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(password),
    },
  ];

  const isCommon = COMMON_PASSWORDS.has(password.toLowerCase());
  if (isCommon) {
    checks.push({ label: 'Not a common password', met: false });
  }

  const score = checks.filter((c) => c.met).length;
  const isValid = checks.every((c) => c.met) && !isCommon;

  let strengthLabel: string;
  if (score <= 1) strengthLabel = 'Very Weak';
  else if (score === 2) strengthLabel = 'Weak';
  else if (score === 3) strengthLabel = 'Fair';
  else if (score === 4) strengthLabel = 'Strong';
  else strengthLabel = 'Very Strong';

  if (isCommon) strengthLabel = 'Common Password';

  return { isValid, score, checks, strengthLabel };
}

export function getStrengthColor(score: number): string {
  if (score <= 1) return '#ef4444'; // red
  if (score === 2) return '#f97316'; // orange
  if (score === 3) return '#eab308'; // yellow
  if (score === 4) return '#22c55e'; // green
  return '#16a34a'; // dark green
}
